// Ürün ve Stok sayfası kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'inventory.list';

export const inventoryListGuide = (t: TFunction): GuideContent => ({
  layout: 'list',
  title: t(`${G}.title`, 'Ürün ve Stok ekranı nasıl kullanılır?'),
  intro: t(`${G}.intro`, 'Bu ekran, ürün kartlarınızı tek yerden takip etmeniz ve stok süreçlerini hızlıca yönetmeniz için kullanılır. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    stats: t(`${G}.regions.stats`, 'Özet kartları'),
    filters: t(`${G}.regions.filters`, 'Arama ve filtre'),
    toolbar: t(`${G}.regions.toolbar`, 'Yeni Stok ve ⋮ menüsü'),
    table: t(`${G}.regions.table`, 'Ürün listesi'),
    pagination: t(`${G}.regions.pagination`, 'Görünüm ve sayfalama'),
  },
  steps: [
    {
      region: 'header',
      title: t(`${G}.s1.title`, '1. Bu ekran ne işe yarar?'),
      text: t(`${G}.s1.text`, 'Ürün kartlarınızı tek yerden takip eder, stok giriş-çıkışlarını, fiyatları ve satış kanallarını buradan yönetirsiniz. Günlük işlerin çoğu bu ekrandan başlar.'),
      tips: [
        t(`${G}.s1.tip1`, 'Öneri: Önce filtre ve tablo görünümünü işinize göre ayarlayıp sonra toplu işlem yapmanız, günlük operasyonu daha hızlı ve hatasız ilerletir.'),
      ],
    },
    {
      region: 'stats',
      title: t(`${G}.s2.title`, '2. Özet kartlarını okuyun'),
      text: t(`${G}.s2.text`, 'Üstteki kartlar stoğunuzun alış ve satış fiyatına göre toplam değerini, son 30 günde en çok satılan ürünü ve 90 gündür hareket görmeyen ürün sayısını gösterir.'),
      tips: [
        t(`${G}.s2.tip1`, '"Hareketsiz Ürün" kartına tıklayınca bu ürünlerin listesi açılır.'),
      ],
    },
    {
      region: 'filters',
      title: t(`${G}.s3.title`, '3. Ürünü bulun'),
      text: t(`${G}.s3.text`, '"Stok Ara" kutusuna aradığınız ürünü yazın. Yanındaki filtre düğmesi ayrıntılı aramayı açar; seçimleriniz "Filtreleri Uygula" ile listeye yansır.'),
      tips: [
        t(`${G}.s3.tip1`, 'Ayrıntılı aramada kategori / marka, depo / lokasyon / şube, KDV / ÖKC, fiyat ve miktar aralığı, oluşturma tarihi, e-ticaret durumu ve "yalnız resimsiz ürünler" seçenekleri vardır.'),
        t(`${G}.s3.tip2`, 'Etkin filtreler listenin üstünde etiket olarak görünür; tek tek kaldırabilir ya da "Filtreleri Temizle" ile hepsini kaldırabilirsiniz.'),
        t(`${G}.s3.tip3`, 'Mobilde kamera düğmesiyle barkod okutarak ürünü bulabilirsiniz.'),
      ],
    },
    {
      region: 'toolbar',
      title: t(`${G}.s4.title`, '4. Yeni ürün ekleyin'),
      text: t(`${G}.s4.text`, '"Yeni Stok" ile tek bir ürünü tüm ayrıntılarıyla kaydedin, "Hızlı Stok Kaydı" ile pratik giriş yapın. Çok sayıda ürün için ⋮ menüsündeki "Stok Aktar" ile Excel\'den toplu kayıt yapın. Yenile düğmesi listeyi tazeler.'),
      tips: [
        t(`${G}.s4.tip1`, 'Ürün kartını doldururken temel bilgi, fiyat ve vergi alanlarını birlikte tamamlayın; böylece hatalı kayıt riski azalır.'),
      ],
    },
    {
      region: 'toolbar',
      title: t(`${G}.s5.title`, '5. ⋮ menüsü ve toplu işlemler'),
      text: t(`${G}.s5.text`, '⋮ menüsünden raporlara, tablo özelleştirmeye, toplu işlemlere ve dışa aktarmaya ulaşırsınız. "Toplu İşlemler"i açınca satırlarda seçim kutuları çıkar; seçtiğiniz ürünlere tek seferde işlem yaparsınız.'),
      tips: [
        t(`${G}.s5.tip1`, 'Menüde: Raporlar, Kategoriler, Ek Özellikler, Markalar, Depolar, Basit Üretim Reçeteleri, Menüler, Resim Kütüphanesi, Tabloyu Özelleştir, Kargo Şablonları, Excel İndir, Stok Aktar, Etiket Yazdırma, Tartı Senkronu ve pazaryeri katalogları.'),
        t(`${G}.s5.tip2`, 'Toplu işlemler: silme, pasife alma, satış / alış fiyatı güncelleme, e-ticarete / QR menüye / kiosk\'a açma, depoya atama, KDV ve ÖKC güncelleme, toplu resim silme.'),
        t(`${G}.s5.tip3`, 'Pazaryeri İşlem Logları ve Fiyat Değişim Logları ile yapılan değişiklikleri geriye dönük takip edin.'),
        t(`${G}.s5.tip4`, 'Deposu olmayan ürün varsa listenin üstünde uyarı çıkar; miktar güncellemesi ve depo transferi için "Hepsini Merkez Depo\'ya Taşı" ile önce depoya atayın.'),
      ],
    },
    {
      region: 'table',
      title: t(`${G}.s6.title`, '6. Satırdaki işlemleri kullanın'),
      text: t(`${G}.s6.text`, 'Satırın sonundaki ⋮ düğmesi o ürünün işlemlerini açar. Stok hareketi, şube fiyatı, menü / reçete ve satış kanalı işlemlerini ürün bazında buradan yönetirsiniz.'),
      tips: [
        t(`${G}.s6.tip1`, 'Hareket Raporu, Stok Ayrıntıları, Stok Hareketi, Şube Fiyatı, Basit Üretim, Menü Tanımları, Düzenle, Sil.'),
        t(`${G}.s6.tip2`, 'Varyantları Yönet yalnız ana ürünlerde etkindir.'),
        t(`${G}.s6.tip3`, 'Satış Kanallarına Aç: E-Ticaret, N11, QR Menü ve Kiosk.'),
        t(`${G}.s6.tip4`, 'Pasif bir üründe önce "Aktif Et" ile ürünü aktifleştirmeniz gerekir.'),
      ],
    },
    {
      region: 'pagination',
      title: t(`${G}.s7.title`, '7. Görünümü ayarlayın, sayfalar arasında gezin'),
      text: t(`${G}.s7.text`, 'Tablonun üstündeki anahtarlarla stoğu olmayan, pasif veya fiyatı 0 olan ürünleri gizleyebilir, listeyi yeniden eskiye sıralayabilir ve alt varyantları gösterebilirsiniz. Seçimleriniz hatırlanır. Liste uzunsa sayfalama ile ilerleyin ve sayfa başına satır sayısını seçin.'),
      tips: [
        t(`${G}.s7.tip1`, 'Hangi sütunların görüneceğini ⋮ menüsündeki "Tabloyu Özelleştir"den seçin.'),
        t(`${G}.s7.tip2`, 'Başlıktaki (i) düğmesi bu rehberi her zaman yeniden açar.'),
      ],
    },
  ],
});
